import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { api } from '@/api'
import type {
  Client,
  CopyDoc,
  EntityType,
  FacultyMemberInput,
  MarkKind,
  MediaItem,
  NewClient,
  PartnerFact,
  ProgramPatch,
  Quote,
  Reviewable,
  ReviewStatus,
  Segment,
  SegmentPatch,
  ContentOwner,
  ContentPatch,
  WbProgramPatch,
  WbProgramTarget,
} from '@/types'

/**
 * Every client-scoped key puts clientId at index 1. That single invariant is
 * what makes cache purging on client switch a one-line predicate, and it is
 * the reason a stale tenant's rows can never render under another tenant.
 */
export const qk = {
  session: () => ['session'] as const,
  clients: () => ['clients'] as const,
  /** Global lookup — deliberately not in CLIENT_SCOPED, so a client switch keeps it. */
  programTypes: () => ['program-types'] as const,
  /** Global too: the same list for every client, though the route sits under one. */
  defaultSegments: () => ['default-segments'] as const,
  /** Global, as `defaultSegments` (PC-31). */
  suggestedPrograms: () => ['suggested-programs'] as const,
  client: (clientId: string) => ['client', clientId] as const,
  reviewSummary: (clientId: string) => ['review-summary', clientId] as const,
  tags: (clientId: string) => ['tags', clientId] as const,
  crawlRun: (clientId: string) => ['crawl-run', clientId] as const,
  pages: (clientId: string) => ['pages', clientId] as const,
  segments: (clientId: string) => ['segments', clientId] as const,
  faculty: (clientId: string) => ['faculty', clientId] as const,
  programs: (clientId: string) => ['programs', clientId] as const,
  program: (clientId: string, programId?: string) =>
    (programId ? ['program', clientId, programId] : ['program', clientId]) as readonly string[],
  slatePrograms: (clientId: string) => ['slate-programs', clientId] as const,
  wbPrograms: (clientId: string) => ['wb-programs', clientId] as const,
  programContent: (clientId: string, owner?: ContentOwner, ownerId?: string) =>
    (owner && ownerId ? ['program-content', clientId, owner, ownerId] : ['program-content', clientId]) as readonly string[],
  contentSummaries: (clientId: string) => ['program-content-summary', clientId] as const,
  quotes: (clientId: string) => ['quotes', clientId] as const,
  quote: (clientId: string, quoteId?: string) =>
    (quoteId ? ['quote', clientId, quoteId] : ['quote', clientId]) as readonly string[],
  media: (clientId: string) => ['media', clientId] as const,
  mediaAsset: (clientId: string, assetId?: string) =>
    (assetId ? ['media-asset', clientId, assetId] : ['media-asset', clientId]) as readonly string[],
  facts: (clientId: string) => ['facts', clientId] as const,
  copy: (clientId: string) => ['copy', clientId] as const,
}

const CLIENT_SCOPED = new Set([
  'client',
  'review-summary',
  'tags',
  'crawl-run',
  'pages',
  'segments',
  'faculty',
  'programs',
  'program',
  'slate-programs',
  'wb-programs',
  'program-content',
  'program-content-summary',
  'quotes',
  'quote',
  'media',
  'media-asset',
  'facts',
  'copy',
])

/** Drops every cached entry belonging to any client other than `keepClientId`. */
export function purgeOtherClients(qc: QueryClient, keepClientId: string | null) {
  qc.removeQueries({
    predicate: (query) => {
      const [root, scope] = query.queryKey as [string, string | undefined]
      if (!CLIENT_SCOPED.has(root)) return false
      return typeof scope === 'string' && scope !== keepClientId
    },
  })
}

/* ---------------------------------------------------------------- reads */

export function useClients() {
  return useQuery({ queryKey: qk.clients(), queryFn: api.listClients })
}

export function useClient(clientId: string) {
  return useQuery({ queryKey: qk.client(clientId), queryFn: () => api.getClient(clientId) })
}

export function useReviewSummary(clientId: string | null) {
  return useQuery({
    queryKey: qk.reviewSummary(clientId ?? '_'),
    queryFn: () => api.getReviewSummary(clientId!),
    enabled: Boolean(clientId),
  })
}

export function useClientTags(clientId: string) {
  return useQuery({ queryKey: qk.tags(clientId), queryFn: () => api.listClientTags(clientId) })
}

export function useCrawlRun(clientId: string) {
  return useQuery({ queryKey: qk.crawlRun(clientId), queryFn: () => api.getLatestCrawlRun(clientId) })
}

export function usePages(clientId: string) {
  return useQuery({ queryKey: qk.pages(clientId), queryFn: () => api.listPages(clientId) })
}

export function useProgramTypes() {
  return useQuery({ queryKey: qk.programTypes(), queryFn: api.listProgramTypes, staleTime: Infinity })
}

export function useSegments(clientId: string) {
  return useQuery({ queryKey: qk.segments(clientId), queryFn: () => api.listSegments(clientId) })
}

/** The default segment names (PC-20). Code on the server, so it never goes stale in a session. */
export function useDefaultSegments(clientId: string) {
  return useQuery({
    queryKey: qk.defaultSegments(),
    queryFn: () => api.listDefaultSegments(clientId),
    staleTime: Infinity,
  })
}

/** The suggested programs (PC-31). Code on the server, as the default segments are. */
export function useSuggestedPrograms(clientId: string) {
  return useQuery({
    queryKey: qk.suggestedPrograms(),
    queryFn: () => api.listSuggestedPrograms(clientId),
    staleTime: Infinity,
  })
}

export function usePrograms(clientId: string) {
  return useQuery({ queryKey: qk.programs(clientId), queryFn: () => api.listPrograms(clientId) })
}

/** Detail only: status, flag and tags stay on the list row, which mutations update. */
export function useProgram(clientId: string, programId: string | null) {
  return useQuery({
    queryKey: qk.program(clientId, programId ?? ''),
    queryFn: () => api.getProgram(clientId, programId!),
    enabled: programId !== null,
  })
}

export function useSlatePrograms(clientId: string) {
  return useQuery({ queryKey: qk.slatePrograms(clientId), queryFn: () => api.listSlatePrograms(clientId) })
}

export function useWbPrograms(clientId: string) {
  return useQuery({ queryKey: qk.wbPrograms(clientId), queryFn: () => api.listWbPrograms(clientId) })
}

/** What a WB program or slate program shows (PC-9). Null `ownerId` reads nothing. */
export function useProgramContent(clientId: string, owner: ContentOwner, ownerId: string | null) {
  return useQuery({
    queryKey: qk.programContent(clientId, owner, ownerId ?? undefined),
    queryFn: () => api.getProgramContent(clientId, owner, ownerId!),
    enabled: ownerId !== null,
  })
}

/** Every owner's shown content status, for the tables' Content column. */
export function useContentSummaries(clientId: string) {
  return useQuery({ queryKey: qk.contentSummaries(clientId), queryFn: () => api.listContentSummaries(clientId) })
}

/**
 * Content edits for one owner. Every write invalidates all of the client's
 * content: a WB program's reaches the slate programs that inherit it.
 */
export function useProgramContentMutations(clientId: string, owner: ContentOwner, ownerId: string) {
  const qc = useQueryClient()
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: qk.programContent(clientId) })
    qc.invalidateQueries({ queryKey: qk.contentSummaries(clientId) })
  }
  return {
    update: useMutation({
      mutationFn: (patch: ContentPatch) => api.updateProgramContent(clientId, owner, ownerId, patch),
      onSuccess: invalidate,
    }),
    setStatus: useMutation({
      mutationFn: ({ status, override }: { status: 'ready' | 'in_review'; override: boolean }) =>
        api.setProgramContentStatus(clientId, owner, ownerId, status, override),
      onSuccess: invalidate,
    }),
    createOwn: useMutation({
      mutationFn: (copyFromWbProgram: boolean) => api.createSlateContent(clientId, ownerId, copyFromWbProgram),
      onSuccess: invalidate,
    }),
    removeOwn: useMutation({
      mutationFn: () => api.deleteSlateContent(clientId, ownerId),
      onSuccess: invalidate,
    }),
  }
}

export function useQuotes(clientId: string) {
  return useQuery({ queryKey: qk.quotes(clientId), queryFn: () => api.listQuotes(clientId) })
}

/** Detail only, as `useProgram`: the editable fields stay on the list row. */
export function useQuote(clientId: string, quoteId: string | null) {
  return useQuery({
    queryKey: qk.quote(clientId, quoteId ?? ''),
    queryFn: () => api.getQuote(clientId, quoteId!),
    enabled: quoteId !== null,
  })
}

export function useMedia(clientId: string) {
  return useQuery({ queryKey: qk.media(clientId), queryFn: () => api.listMedia(clientId) })
}

/** Detail only, as `useProgram`: the editable fields stay on the list row. */
export function useMediaAsset(clientId: string, assetId: string | null) {
  return useQuery({
    queryKey: qk.mediaAsset(clientId, assetId ?? ''),
    queryFn: () => api.getMedia(clientId, assetId!),
    enabled: assetId !== null,
  })
}

export function useFacts(clientId: string) {
  return useQuery({ queryKey: qk.facts(clientId), queryFn: () => api.listFacts(clientId) })
}

export function useCopyDocs(clientId: string) {
  return useQuery({ queryKey: qk.copy(clientId), queryFn: () => api.listCopyDocs(clientId) })
}

const ENTITY_READERS: Record<EntityType, (clientId: string) => Promise<Reviewable[]>> = {
  programs: api.listPrograms,
  quotes: api.listQuotes,
  media: api.listMedia,
  facts: api.listFacts,
}

/**
 * Entity-type-parameterised read, so `useReviewCollection` can serve all four
 * review surfaces from one hook without calling — and therefore fetching —
 * every list on every page.
 *
 * The key is `[entity, clientId]`, matching the per-entity keys above, so
 * mutations invalidate this and the typed hooks together.
 */
export function useEntityList(clientId: string, entity: EntityType) {
  return useQuery({
    queryKey: [entity, clientId] as const,
    queryFn: () => ENTITY_READERS[entity](clientId),
  })
}

/* ------------------------------------------------------------ mutations */

function entityKey(clientId: string, entity: EntityType) {
  return [entity, clientId] as const
}

/**
 * One review mutation for all four review surfaces. Optimistic, because
 * approve/reject is cheap and reversible; invalidates the review summary so
 * the nav badges stay honest.
 */
export function useReviewMutation(clientId: string, entity: EntityType) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ ids, status }: { ids: string[]; status: ReviewStatus }) =>
      api.setStatus(clientId, entity, ids, status),
    onMutate: async ({ ids, status }) => {
      const key = entityKey(clientId, entity)
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData(key)
      qc.setQueryData(key, (rows: Array<{ id: string; status: ReviewStatus }> | undefined) =>
        rows?.map((r) => (ids.includes(r.id) ? { ...r, status, previouslyRejected: false } : r)),
      )
      return { prev, key }
    },
    onError: (_e, _v, ctx) => {
      if (ctx) qc.setQueryData(ctx.key, ctx.prev)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: entityKey(clientId, entity) })
      qc.invalidateQueries({ queryKey: qk.reviewSummary(clientId) })
      qc.invalidateQueries({ queryKey: qk.clients() })
      // A segment shows its featured quote only while it is approved (PC-24).
      if (entity === 'quotes') qc.invalidateQueries({ queryKey: qk.segments(clientId) })
    },
  })
}

/** Favorite or flag, set or cleared. `kind` is also the row field it flips. */
export function useMarkMutation(clientId: string, entity: EntityType, kind: MarkKind) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, on }: { id: string; on: boolean }) => api.setMark(clientId, entity, id, kind, on),
    onMutate: async ({ id, on }) => {
      const key = entityKey(clientId, entity)
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData(key)
      qc.setQueryData(key, (rows: Array<{ id: string } & Record<MarkKind, boolean>> | undefined) =>
        rows?.map((r) => (r.id === id ? { ...r, [kind]: on } : r)),
      )
      return { prev, key }
    },
    onError: (_e, _v, ctx) => {
      if (ctx) qc.setQueryData(ctx.key, ctx.prev)
    },
    onSettled: () => qc.invalidateQueries({ queryKey: entityKey(clientId, entity) }),
  })
}

export function useTagMutation(clientId: string, entity: EntityType) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ ids, tags }: { ids: string[]; tags: string[] }) => api.setTags(clientId, entity, ids, tags),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: entityKey(clientId, entity) })
      qc.invalidateQueries({ queryKey: qk.tags(clientId) })
    },
  })
}

export function useAddTagMutation(clientId: string, entity: EntityType) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ ids, tag }: { ids: string[]; tag: string }) => api.addTagToMany(clientId, entity, ids, tag),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: entityKey(clientId, entity) })
      qc.invalidateQueries({ queryKey: qk.tags(clientId) })
    },
  })
}

/** No optimistic update — delete is destructive and not worth reversing badly. */
export function useDeleteMutation(clientId: string, entity: EntityType) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ids: string[]) => api.deleteEntities(entity, ids),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: entityKey(clientId, entity) })
      qc.invalidateQueries({ queryKey: qk.reviewSummary(clientId) })
    },
  })
}

export function useCreateClient() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (client: NewClient) => api.createClient(client),
    onSuccess: (client) => {
      qc.setQueryData(qk.client(client.id), client)
      qc.invalidateQueries({ queryKey: qk.clients() })
    },
  })
}

export function useUpdateClient(clientId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (patch: Partial<Client>) => api.updateClient(clientId, patch),
    onSuccess: (client) => {
      qc.setQueryData(qk.client(clientId), client)
      qc.invalidateQueries({ queryKey: qk.clients() })
    },
  })
}

export function useSegmentMutations(clientId: string) {
  const qc = useQueryClient()
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: qk.segments(clientId) })
    qc.invalidateQueries({ queryKey: qk.programs(clientId) })
  }
  return {
    create: useMutation({ mutationFn: (name: string) => api.createSegment(clientId, name), onSuccess: invalidate }),
    rename: useMutation({
      mutationFn: ({ id, name }: { id: string; name: string }) => api.renameSegment(clientId, id, name),
      onSuccess: invalidate,
    }),
    /** Name and details (PC-24) in one PATCH. */
    update: useMutation({
      mutationFn: ({ id, patch }: { id: string; patch: SegmentPatch }) =>
        api.updateSegment(clientId, id, patch),
      // The saved row at once, so reopening the details before the refetch lands shows it.
      onSuccess: (saved) => {
        qc.setQueryData(qk.segments(clientId), (rows: Segment[] | undefined) =>
          rows?.map((s) => (s.id === saved.id ? saved : s)),
        )
        invalidate()
      },
    }),
    move: useMutation({
      mutationFn: ({ id, direction }: { id: string; direction: -1 | 1 }) =>
        api.moveSegment(clientId, id, direction),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: ({ id, reassignTo }: { id: string; reassignTo: string | null }) =>
        api.deleteSegment(clientId, id, reassignTo),
      onSuccess: invalidate,
    }),
  }
}

/** Every faculty member, inactive ones included: pickers offer only active ones (PC-24). */
export function useFaculty(clientId: string) {
  return useQuery({ queryKey: qk.faculty(clientId), queryFn: () => api.listFaculty(clientId) })
}

export function useFacultyMutations(clientId: string) {
  const qc = useQueryClient()
  // Segments embed the faculty member they show.
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: qk.faculty(clientId) })
    qc.invalidateQueries({ queryKey: qk.segments(clientId) })
  }
  return {
    create: useMutation({
      mutationFn: (input: FacultyMemberInput) => api.createFacultyMember(clientId, input),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, patch }: { id: string; patch: FacultyMemberInput }) =>
        api.updateFacultyMember(clientId, id, patch),
      onSuccess: invalidate,
    }),
    /** The Portal's "delete": a soft delete, undone by `update` with `active: true` (migrations/027). */
    remove: useMutation({
      mutationFn: (id: string) => api.updateFacultyMember(clientId, id, { active: false }),
      onSuccess: invalidate,
    }),
  }
}

/**
 * Every list a WB program write can change: the WB programs, the crawled rows
 * that copy its name and segment, and the slate programs that name it.
 */
function invalidateWbPrograms(qc: ReturnType<typeof useQueryClient>, clientId: string) {
  qc.invalidateQueries({ queryKey: qk.wbPrograms(clientId) })
  qc.invalidateQueries({ queryKey: qk.programs(clientId) })
  qc.invalidateQueries({ queryKey: qk.program(clientId) })
  qc.invalidateQueries({ queryKey: qk.slatePrograms(clientId) })
  qc.invalidateQueries({ queryKey: qk.programContent(clientId) })
  qc.invalidateQueries({ queryKey: qk.contentSummaries(clientId) })
}

/** WB program create, edit, bulk segment and delete (PC-33). */
export function useWbProgramMutations(clientId: string) {
  const qc = useQueryClient()
  const invalidate = () => invalidateWbPrograms(qc, clientId)
  return {
    create: useMutation({
      mutationFn: (input: { name: string; segmentId?: string | null }) => api.createWbProgram(clientId, input),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, patch }: { id: string; patch: WbProgramPatch }) => api.updateWbProgram(clientId, id, patch),
      onSuccess: invalidate,
    }),
    setSegment: useMutation({
      mutationFn: ({ ids, segmentId }: { ids: string[]; segmentId: string | null }) =>
        api.setWbProgramSegment(clientId, ids, segmentId),
      onSettled: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.deleteWbProgram(clientId, id),
      onSuccess: invalidate,
    }),
  }
}

/** Crawled programs under a WB program, a new one by name, or none (PC-33). */
export function useAssignPrograms(clientId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ ids, target }: { ids: string[]; target: WbProgramTarget }) => api.assignPrograms(clientId, ids, target),
    onSettled: () => invalidateWbPrograms(qc, clientId),
  })
}

export function useUpsertProgram(clientId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (patch: ProgramPatch) => api.upsertProgram(clientId, patch),
    onSuccess: () => {
      // A new degree, or a level change (PC-25), moves a row into or out of a WB program.
      invalidateWbPrograms(qc, clientId)
      qc.invalidateQueries({ queryKey: qk.reviewSummary(clientId) })
    },
  })
}

/** Maps a slate program to a WB program, or unmaps it (PC-33). */
export function useMapSlateProgram(clientId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ slateProgramId, wbProgramId }: { slateProgramId: string; wbProgramId: string | null }) =>
      api.mapSlateProgram(clientId, slateProgramId, wbProgramId),
    onSuccess: () => invalidateWbPrograms(qc, clientId),
  })
}

export function useUpsertQuote(clientId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (patch: Partial<Quote> & { id?: string }) => api.upsertQuote(clientId, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.quotes(clientId) })
      qc.invalidateQueries({ queryKey: qk.quote(clientId) })
      qc.invalidateQueries({ queryKey: qk.reviewSummary(clientId) })
    },
  })
}

export function useUpsertMedia(clientId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (patch: Partial<MediaItem> & { id?: string }) => api.upsertMedia(clientId, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.media(clientId) })
      qc.invalidateQueries({ queryKey: qk.mediaAsset(clientId) })
      qc.invalidateQueries({ queryKey: qk.reviewSummary(clientId) })
    },
  })
}

export function useUpsertFact(clientId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (patch: Partial<PartnerFact> & { id?: string }) => api.upsertFact(clientId, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.facts(clientId) })
      qc.invalidateQueries({ queryKey: qk.reviewSummary(clientId) })
    },
  })
}

export function useCopyMutations(clientId: string) {
  const qc = useQueryClient()
  const invalidate = () => qc.invalidateQueries({ queryKey: qk.copy(clientId) })
  return {
    save: useMutation({
      mutationFn: (patch: Partial<CopyDoc> & { id?: string }) => api.upsertCopyDoc(clientId, patch),
      onSuccess: invalidate,
    }),
    favorite: useMutation({
      mutationFn: ({ id, favorite }: { id: string; favorite: boolean }) =>
        api.setCopyFavorite(id, favorite),
      onSuccess: invalidate,
    }),
    remove: useMutation({ mutationFn: (id: string) => api.deleteCopyDoc(id), onSuccess: invalidate }),
  }
}

export function useCrawlMutations(clientId: string) {
  const qc = useQueryClient()
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: qk.pages(clientId) })
    qc.invalidateQueries({ queryKey: qk.crawlRun(clientId) })
  }
  return {
    start: useMutation({ mutationFn: () => api.startCrawl(clientId), onSuccess: invalidate }),
    exclude: useMutation({ mutationFn: (pageId: string) => api.excludePage(pageId), onSuccess: invalidate }),
    retry: useMutation({ mutationFn: (pageId: string) => api.retryPage(pageId), onSuccess: invalidate }),
    setImportance: useMutation({
      mutationFn: ({ pageId, importance }: { pageId: string; importance: number }) =>
        api.setPageImportance(pageId, importance),
      onSuccess: invalidate,
    }),
  }
}
